/* ============================================================
   storage — a small dropdown.

   Two buttons in the toolbar open one: New (folder, secure
   folder) and Upload (files, folder). Grouping them is what
   keeps the toolbar to two controls instead of five competing
   ones, so the menu has to behave: click outside or Escape
   closes it, arrows move, Enter picks, focus comes back.
   ============================================================ */

import React, { useEffect, useRef, useState } from 'react';
import { Icon } from './lib';

export default function Menu({ label, icon, items, primary, disabled, align = 'right' }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  const btn = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!wrap.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => {
      if (e.key === 'Escape') { setOpen(false); btn.current?.focus(); return; }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      e.preventDefault();
      const opts = [...wrap.current.querySelectorAll('.st-menu-item:not(:disabled)')];
      const at = opts.indexOf(document.activeElement);
      const next = e.key === 'ArrowDown' ? (at + 1) % opts.length : (at - 1 + opts.length) % opts.length;
      opts[next]?.focus();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  return (
    <div className="st-menu-wrap" ref={wrap}>
      <button
        ref={btn}
        type="button"
        className={`btn btn--sm ${primary ? '' : 'btn--ghost'} st-menu-btn`}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
      >
        {icon && <Icon name={icon} size={14}/>}
        {label}
        <Icon name="chevron" size={12} className="st-menu-caret"/>
      </button>
      {open && (
        <div className={`st-menu st-menu--${align}`} role="menu">
          {items.map((it) => (
            <button
              key={it.label}
              type="button"
              role="menuitem"
              className="st-menu-item"
              disabled={it.disabled}
              onClick={() => { setOpen(false); it.onSelect(); }}
            >
              <Icon name={it.icon} size={15}/>
              <span className="st-menu-text">
                {it.label}
                {it.hint && <span className="st-menu-hint">{it.hint}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
